import { Modal, Switch } from '@lib/components'
import { FilePng } from '@lib/images'
import { Button } from '@octopus-do/octopus-storybook'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { useModalExport } from '@storeOld/hooks'
import type React from 'react'
import { useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import type { ModalProps } from '@/types/modal'
import { useScaleEvents } from '../../../ArtBoard/Canvas/hooks/events'
import css from './ExportAsPng.module.scss'
import { usePngExportProps } from './hooks'

const optionsToVisible = {
  transparent: true,
  x2resolution: true,
  logoAndTitle: false,
  colorLegend: true,
}

const ExportAsPng: React.FC<ModalProps> = ({ onClose, onBack }) => {
  const isLegendEnabled = useSelector(
    migrationProjectSelector.getProjectIsLegendEnabled
  )

  const handleClose = () => {
    onClose()
  }

  const { projectName } = useModalExport()
  const { actions, props, save, isSaving, overSized } = usePngExportProps(
    isLegendEnabled,
    handleClose,
    projectName
  )

  const { onResetZoom } = useScaleEvents()

  const onSave = () => {
    sendGtmEvent(GtmTriggerName.EXPORT_PNG, 'Click on export as PNG')

    onResetZoom()

    setTimeout(() => {
      save()
    }, 200)
  }

  return (
    <Modal
      title="Export to PNG"
      onClose={onClose}
      onBack={onBack}
      width={352}
      image={<FilePng />}
    >
      <div className={css.options}>
        {optionsToVisible.transparent && (
          <div className={css.option}>
            <span className={css.title}>Transparent</span>
            <Switch
              checked={props.transparent}
              onChange={actions.setTransparent}
            />
          </div>
        )}

        {optionsToVisible.x2resolution && (
          <div className={css.option}>
            <span className={css.title}>x2 resolution</span>
            <Switch
              disabled={overSized}
              checked={props.x2resolution}
              onChange={actions.setX2resolution}
            />
          </div>
        )}

        {optionsToVisible.logoAndTitle && (
          <div className={css.option}>
            <span className={css.title}>Project title</span>
            <Switch
              checked={props.logoAndTitle}
              onChange={actions.setLogoAndTitle}
            />
          </div>
        )}

        {optionsToVisible.colorLegend && isLegendEnabled && (
          <div className={css.option}>
            <span className={css.title}>Color Legend</span>
            <Switch
              checked={props.colorLegend}
              onChange={actions.setColorLegend}
            />
          </div>
        )}
      </div>

      <div className={css.footer}>
        <Button label="Generate" loading={isSaving} onClick={onSave} />
      </div>
    </Modal>
  )
}

export default ExportAsPng
